import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Plus, Edit, Trash2, Search, BookOpen, Users, User, Copy, Dumbbell, Clock, RotateCcw, Target, LayoutGrid, List, FileSpreadsheet, Download, Upload, CheckCircle } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const ExerciseLibraryTab = ({ t, currentUser }) => {
  const [customExercises, setCustomExercises] = useState([]);
  const [prebuiltExercises, setPrebuiltExercises] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterDiscipline, setFilterDiscipline] = useState('all');
  const [viewMode, setViewMode] = useState('custom'); // 'custom', 'prebuilt', 'all'
  const [displayMode, setDisplayMode] = useState('grid'); // 'grid' or 'list'
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState(null);
  
  // Import state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [importing, setImporting] = useState(false);
  const importInputRef = useRef(null);
  
  const [exerciseForm, setExerciseForm] = useState({
    name: '',
    type: 'Warm-up',
    description: '',
    duration_minutes: '',
    repetitions: '',
    distance_meters: '',
    rest_seconds: '',
    instructions: '',
    difficulty_level: 1,
    category: '',
    target_discipline: '',
    is_public: false
  });

  const exerciseTypes = [
    { value: 'Warm-up', label: t('warmUp') },
    { value: 'Breath Hold', label: t('breathHold') },
    { value: 'Dynamic', label: t('dynamic') },
    { value: 'Static', label: t('static') },
    { value: 'Recovery', label: t('recovery') },
    { value: 'Cool Down', label: t('coolDown') },
    { value: 'Technique', label: t('technique') }
  ];

  const disciplines = ['STA', 'DYN', 'DYNBF', 'DNF', 'CWT', 'CNF', 'FIM', 'Mix'];
  
  const difficultyLevels = [
    { value: 1, label: '⭐ Beginner' },
    { value: 2, label: '⭐⭐ Easy' },
    { value: 3, label: '⭐⭐⭐ Intermediate' },
    { value: 4, label: '⭐⭐⭐⭐ Advanced' },
    { value: 5, label: '⭐⭐⭐⭐⭐ Expert' }
  ];

  // Fetch exercises on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch custom exercises
        const customResponse = await axios.get(`${API_BASE_URL}/api/custom-exercises`, {
          params: { user_id: currentUser?.id, include_public: true }
        });
        setCustomExercises(customResponse.data.exercises || []);
        
        // Fetch prebuilt exercises and flatten them (they're nested by category)
        const prebuiltResponse = await axios.get(`${API_BASE_URL}/api/exercise-library`);
        const exercisesByCategory = prebuiltResponse.data.exercises || {};
        const flattenedExercises = [];
        Object.entries(exercisesByCategory).forEach(([category, exercises]) => {
          exercises.forEach(exercise => {
            flattenedExercises.push({ ...exercise, category });
          });
        });
        setPrebuiltExercises(flattenedExercises);
        
        // Fetch categories
        const categoriesResponse = await axios.get(`${API_BASE_URL}/api/exercise-categories`);
        setCategories(categoriesResponse.data.categories || []);
      } catch (error) {
        console.error('Error fetching exercises:', error);
      }
    };
    
    fetchData();
  }, [currentUser?.id]);

  const refetchExercises = async () => {
    try {
      const customResponse = await axios.get(`${API_BASE_URL}/api/custom-exercises`, {
        params: { user_id: currentUser?.id, include_public: true }
      });
      setCustomExercises(customResponse.data.exercises || []);
      
      const categoriesResponse = await axios.get(`${API_BASE_URL}/api/exercise-categories`);
      setCategories(categoriesResponse.data.categories || []);
    } catch (error) {
      console.error('Error refetching exercises:', error);
    }
  };

  const resetForm = () => {
    setExerciseForm({
      name: '',
      type: 'Warm-up',
      description: '',
      duration_minutes: '',
      repetitions: '',
      distance_meters: '',
      rest_seconds: '',
      instructions: '',
      difficulty_level: 1,
      category: '',
      target_discipline: '',
      is_public: false
    });
  };

  const handleCreateExercise = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...exerciseForm,
        duration_minutes: exerciseForm.duration_minutes ? parseInt(exerciseForm.duration_minutes) : null,
        repetitions: exerciseForm.repetitions ? parseInt(exerciseForm.repetitions) : null,
        distance_meters: exerciseForm.distance_meters ? parseInt(exerciseForm.distance_meters) : null,
        rest_seconds: exerciseForm.rest_seconds ? parseInt(exerciseForm.rest_seconds) : null,
        difficulty_level: parseInt(exerciseForm.difficulty_level)
      };
      
      await axios.post(`${API_BASE_URL}/api/custom-exercises`, data, {
        params: { user_id: currentUser?.id }
      });
      
      setShowCreateModal(false);
      resetForm();
      refetchExercises();
    } catch (error) {
      console.error('Error creating exercise:', error);
      alert('Error creating exercise. Please try again.');
    }
  };

  const handleUpdateExercise = async (e) => {
    e.preventDefault();
    if (!selectedExercise) return;
    
    try {
      const data = {
        ...exerciseForm,
        duration_minutes: exerciseForm.duration_minutes ? parseInt(exerciseForm.duration_minutes) : null,
        repetitions: exerciseForm.repetitions ? parseInt(exerciseForm.repetitions) : null,
        distance_meters: exerciseForm.distance_meters ? parseInt(exerciseForm.distance_meters) : null,
        rest_seconds: exerciseForm.rest_seconds ? parseInt(exerciseForm.rest_seconds) : null,
        difficulty_level: parseInt(exerciseForm.difficulty_level)
      };
      
      await axios.put(`${API_BASE_URL}/api/custom-exercises/${selectedExercise.id}`, data);
      
      setShowEditModal(false);
      setSelectedExercise(null);
      resetForm();
      refetchExercises();
    } catch (error) {
      console.error('Error updating exercise:', error);
      alert('Error updating exercise. Please try again.');
    }
  };

  const handleDeleteExercise = async (exerciseId) => {
    if (!window.confirm(t('confirmDelete'))) return;
    
    try {
      await axios.delete(`${API_BASE_URL}/api/custom-exercises/${exerciseId}`);
      refetchExercises();
    } catch (error) {
      console.error('Error deleting exercise:', error);
      alert('Error deleting exercise. Please try again.');
    }
  };

  const openEditModal = (exercise) => {
    setSelectedExercise(exercise);
    setExerciseForm({
      name: exercise.name || '',
      type: exercise.type || 'Warm-up',
      description: exercise.description || '',
      duration_minutes: exercise.duration_minutes || '',
      repetitions: exercise.repetitions || '',
      distance_meters: exercise.distance_meters || '',
      rest_seconds: exercise.rest_seconds || '',
      instructions: exercise.instructions || '',
      difficulty_level: exercise.difficulty_level || 1,
      category: exercise.category || '',
      target_discipline: exercise.target_discipline || '',
      is_public: exercise.is_public || false
    });
    setShowEditModal(true);
  };

  const duplicateExercise = (exercise) => {
    setExerciseForm({
      name: `${exercise.name} (Copy)`,
      type: exercise.type || 'Warm-up',
      description: exercise.description || '',
      duration_minutes: exercise.duration_minutes || '',
      repetitions: exercise.repetitions || '',
      distance_meters: exercise.distance_meters || '',
      rest_seconds: exercise.rest_seconds || '',
      instructions: exercise.instructions || '',
      difficulty_level: exercise.difficulty_level || 1,
      category: exercise.category || '',
      target_discipline: exercise.target_discipline || '',
      is_public: false
    });
    setShowCreateModal(true);
  };

  // Download Excel template for exercise import
  const downloadExerciseTemplate = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/exercises/template/excel`);
      const { headers, sample_data } = response.data;
      
      // Create CSV content
      let csvContent = headers.join(',') + '\n';
      sample_data.forEach(row => {
        const rowData = headers.map(h => {
          const value = row[h] || '';
          // Escape commas and quotes
          if (String(value).includes(',') || String(value).includes('"')) {
            return `"${String(value).replace(/"/g, '""')}"`;
          }
          return value;
        });
        csvContent += rowData.join(',') + '\n';
      });
      
      // Download as CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'exercise_import_template.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading template:', error);
      alert('Error downloading template. Please try again.');
    }
  };

  // Handle file selection for import
  const handleImportFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImportFile(file);
      setImportResults(null);
    }
  };

  // Import exercises from file
  const importExercises = async () => {
    if (!importFile) return;
    
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('user_id', currentUser?.id || '');
      
      const response = await axios.post(`${API_BASE_URL}/api/exercises/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setImportResults(response.data);
      refetchExercises();
    } catch (error) {
      console.error('Error importing exercises:', error);
      setImportResults({
        success: false,
        message: error.response?.data?.detail || 'Error importing exercises',
        processed: 0,
        created: 0,
        errors: [{ row: 0, error: error.message }]
      });
    } finally {
      setImporting(false);
    }
  };

  // Close import dialog and reset
  const closeImportDialog = () => {
    setShowImportDialog(false);
    setImportFile(null);
    setImportResults(null);
    if (importInputRef.current) {
      importInputRef.current.value = '';
    }
  };

  // Filter exercises
  const getFilteredExercises = () => {
    let exercises = [];
    
    if (viewMode === 'custom' || viewMode === 'all') {
      exercises = [...exercises, ...customExercises.map(e => ({ ...e, source: 'custom' }))];
    }
    if (viewMode === 'prebuilt' || viewMode === 'all') {
      exercises = [...exercises, ...prebuiltExercises.map(e => ({ ...e, source: 'prebuilt' }))];
    }
    
    return exercises.filter(exercise => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          exercise.name?.toLowerCase().includes(query) ||
          exercise.description?.toLowerCase().includes(query) ||
          exercise.category?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      
      // Type filter
      if (filterType !== 'all' && exercise.type !== filterType) return false;
      
      // Category filter
      if (filterCategory !== 'all' && exercise.category !== filterCategory) return false;
      
      // Discipline filter
      if (filterDiscipline !== 'all' && exercise.target_discipline !== filterDiscipline) return false;
      
      return true;
    });
  };

  const filteredExercises = getFilteredExercises();

  const getTypeColor = (type) => {
    const colors = {
      'Warm-up': 'bg-orange-600',
      'Breath Hold': 'bg-blue-600',
      'Dynamic': 'bg-teal-600',
      'Static': 'bg-purple-600',
      'Recovery': 'bg-green-600',
      'Cool Down': 'bg-indigo-600',
      'Technique': 'bg-pink-600'
    };
    return colors[type] || 'bg-gray-600';
  };

  const renderExerciseForm = (onSubmit, submitLabel) => (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-blue-200">{t('exerciseName')} *</Label>
          <Input
            value={exerciseForm.name}
            onChange={(e) => setExerciseForm({ ...exerciseForm, name: e.target.value })}
            placeholder="e.g., 4x25m Dynamic Warm-up"
            className="bg-blue-800/50 border-blue-600 text-white"
            required
          />
        </div>
        <div>
          <Label className="text-blue-200">{t('exerciseType')} *</Label>
          <Select value={exerciseForm.type} onValueChange={(value) => setExerciseForm({ ...exerciseForm, type: value })}>
            <SelectTrigger className="bg-blue-800/50 border-blue-600 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-blue-800 border-blue-600">
              {exerciseTypes.map(type => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div>
        <Label className="text-blue-200">{t('exerciseDescription')} *</Label>
        <Textarea
          value={exerciseForm.description}
          onChange={(e) => setExerciseForm({ ...exerciseForm, description: e.target.value })}
          placeholder="Describe the exercise in detail..."
          className="bg-blue-800/50 border-blue-600 text-white"
          rows={3}
          required
        />
      </div>
      
      <div className="grid grid-cols-4 gap-4">
        <div>
          <Label className="text-blue-200">{t('blockDuration')}</Label>
          <Input
            type="number"
            value={exerciseForm.duration_minutes}
            onChange={(e) => setExerciseForm({ ...exerciseForm, duration_minutes: e.target.value })}
            placeholder="min"
            className="bg-blue-800/50 border-blue-600 text-white"
          />
        </div>
        <div>
          <Label className="text-blue-200">{t('repetitions')}</Label>
          <Input
            type="number"
            value={exerciseForm.repetitions}
            onChange={(e) => setExerciseForm({ ...exerciseForm, repetitions: e.target.value })}
            placeholder="reps"
            className="bg-blue-800/50 border-blue-600 text-white"
          />
        </div>
        <div>
          <Label className="text-blue-200">{t('distance')} (m)</Label>
          <Input
            type="number"
            value={exerciseForm.distance_meters}
            onChange={(e) => setExerciseForm({ ...exerciseForm, distance_meters: e.target.value })}
            placeholder="meters"
            className="bg-blue-800/50 border-blue-600 text-white"
          />
        </div>
        <div>
          <Label className="text-blue-200">{t('restSeconds')}</Label>
          <Input
            type="number"
            value={exerciseForm.rest_seconds}
            onChange={(e) => setExerciseForm({ ...exerciseForm, rest_seconds: e.target.value })}
            placeholder="sec"
            className="bg-blue-800/50 border-blue-600 text-white"
          />
        </div>
      </div>
      
      <div>
        <Label className="text-blue-200">{t('exerciseInstructions')}</Label>
        <Textarea
          value={exerciseForm.instructions}
          onChange={(e) => setExerciseForm({ ...exerciseForm, instructions: e.target.value })}
          placeholder="Step-by-step instructions..."
          className="bg-blue-800/50 border-blue-600 text-white"
          rows={2}
        />
      </div>
      
      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label className="text-blue-200">{t('exerciseCategory')}</Label>
          <Input
            value={exerciseForm.category}
            onChange={(e) => setExerciseForm({ ...exerciseForm, category: e.target.value })}
            placeholder="e.g., Pool, Depth, CO2 Tables"
            className="bg-blue-800/50 border-blue-600 text-white"
            list="categories"
          />
          <datalist id="categories">
            {categories.map(cat => <option key={cat} value={cat} />)}
          </datalist>
        </div>
        <div>
          <Label className="text-blue-200">{t('targetDiscipline')}</Label>
          <Select value={exerciseForm.target_discipline || 'any'} onValueChange={(value) => setExerciseForm({ ...exerciseForm, target_discipline: value === 'any' ? '' : value })}>
            <SelectTrigger className="bg-blue-800/50 border-blue-600 text-white">
              <SelectValue placeholder="Select..." />
            </SelectTrigger>
            <SelectContent className="bg-blue-800 border-blue-600">
              <SelectItem value="any">Any</SelectItem>
              {disciplines.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-blue-200">{t('difficultyLevel')}</Label>
          <Select 
            value={String(exerciseForm.difficulty_level)} 
            onValueChange={(value) => setExerciseForm({ ...exerciseForm, difficulty_level: parseInt(value) })}
          >
            <SelectTrigger className="bg-blue-800/50 border-blue-600 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-blue-800 border-blue-600">
              {difficultyLevels.map(level => (
                <SelectItem key={level.value} value={String(level.value)}>{level.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="flex items-center space-x-3 p-3 bg-blue-900/30 rounded-lg">
        <input
          type="checkbox"
          id="is_public"
          checked={exerciseForm.is_public}
          onChange={(e) => setExerciseForm({ ...exerciseForm, is_public: e.target.checked })}
          className="w-4 h-4 rounded border-blue-500"
        />
        <div>
          <Label htmlFor="is_public" className="text-blue-200 cursor-pointer">{t('makePublic')}</Label>
          <p className="text-xs text-blue-400">{t('publicExerciseHint')}</p>
        </div>
      </div>
      
      <DialogFooter>
        <Button type="submit" className="bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700">
          {submitLabel}
        </Button>
      </DialogFooter>
    </form>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold text-blue-200 flex items-center">
          <BookOpen className="mr-3" size={28} />
          {t('exerciseLibrary')}
        </h2>
        <div className="flex gap-2">
          {/* Import Exercises Button */}
          <Button 
            onClick={() => setShowImportDialog(true)}
            variant="outline"
            className="border-teal-600 text-teal-300 hover:bg-teal-600/20"
            data-testid="import-exercises-btn"
          >
            <FileSpreadsheet className="mr-2" size={18} />
            {t('importExercises') || 'Import Exercises'}
          </Button>
          
          <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
            <DialogTrigger asChild>
              <Button 
                onClick={() => { resetForm(); setShowCreateModal(true); }}
                className="bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 text-white border-none"
              >
                <Plus className="mr-2" size={18} />
                {t('createCustomExercise')}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-gradient-to-br from-blue-900 to-teal-900 border-blue-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-blue-200">{t('createCustomExercise')}</DialogTitle>
                <DialogDescription className="text-blue-300">
                  Create a new exercise for your personal library.
                </DialogDescription>
              </DialogHeader>
              {renderExerciseForm(handleCreateExercise, t('save'))}
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Import Exercises Dialog */}
      <Dialog open={showImportDialog} onOpenChange={closeImportDialog}>
        <DialogContent className="bg-gradient-to-br from-blue-900 to-teal-900 border-blue-700 text-white max-w-lg" data-testid="import-exercises-dialog">
          <DialogHeader>
            <DialogTitle className="text-blue-200 flex items-center">
              <FileSpreadsheet className="mr-2" size={20} />
              {t('importExercises') || 'Import Exercises'}
            </DialogTitle>
            <DialogDescription className="text-blue-300">
              {t('downloadTemplateFirst') || 'Download the template file, fill it with your exercises, then upload it here.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Download Template */}
            <div className="p-4 bg-blue-800/30 rounded-lg border border-blue-700/50">
              <p className="text-sm text-blue-300 mb-2">{t('step') || 'Step'} 1: {t('downloadTemplate') || 'Download Template'}</p>
              <Button 
                onClick={downloadExerciseTemplate}
                variant="outline"
                className="w-full border-teal-600 text-teal-300 hover:bg-teal-600/20"
                data-testid="download-exercise-template-btn"
              >
                <Download className="mr-2" size={16} />
                {t('downloadTemplate') || 'Download Template'}
              </Button>
            </div>
            
            {/* Upload File */}
            <div className="p-4 bg-blue-800/30 rounded-lg border border-blue-700/50">
              <p className="text-sm text-blue-300 mb-2">{t('step') || 'Step'} 2: {t('selectExcelFile') || 'Select Excel or CSV file'}</p>
              <div 
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                  importFile ? 'border-teal-500 bg-teal-900/20' : 'border-blue-600 hover:border-teal-500'
                }`}
                onClick={() => importInputRef.current?.click()}
              >
                <input
                  ref={importInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleImportFileSelect}
                  className="hidden"
                  data-testid="import-file-input"
                />
                {importFile ? (
                  <div className="flex items-center justify-center gap-2 text-teal-300">
                    <CheckCircle size={20} />
                    <span>{importFile.name}</span>
                  </div>
                ) : (
                  <div className="text-blue-400">
                    <Upload className="mx-auto mb-2" size={24} />
                    <p>{t('clickToSelectFile') || 'Click to select file'}</p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Import Results */}
            {importResults && (
              <div className={`p-4 rounded-lg ${importResults.created > 0 ? 'bg-green-900/30 border border-green-700' : 'bg-red-900/30 border border-red-700'}`}>
                <h4 className="font-semibold mb-2">{t('importResults') || 'Import Results'}</h4>
                <div className="text-sm space-y-1">
                  <p>{t('processed') || 'Processed'}: {importResults.processed}</p>
                  <p className="text-green-400">{t('created') || 'Created'}: {importResults.created}</p>
                  {importResults.errors?.length > 0 && (
                    <div className="mt-2">
                      <p className="text-red-400">{t('errors') || 'Errors'}: {importResults.errors.length}</p>
                      <ul className="text-red-300 text-xs mt-1 max-h-20 overflow-y-auto">
                        {importResults.errors.slice(0, 5).map((err, i) => (
                          <li key={i}>Row {err.row}: {err.error}</li>
                        ))}
                        {importResults.errors.length > 5 && <li>... and {importResults.errors.length - 5} more</li>}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={closeImportDialog} className="border-blue-600 text-blue-300">
              {t('cancel') || 'Cancel'}
            </Button>
            <Button 
              onClick={importExercises}
              disabled={!importFile || importing}
              className="bg-gradient-to-r from-blue-600 to-teal-600"
              data-testid="import-exercises-submit-btn"
            >
              {importing ? t('importing') || 'Importing...' : t('importExercises') || 'Import Exercises'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Mode Toggle & Filters */}
      <Card className="bg-gradient-to-br from-blue-800/30 to-teal-800/30 border-blue-700/30 backdrop-blur-sm">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* View Mode */}
            <div className="flex items-center bg-blue-900/50 rounded-lg p-1">
              <Button
                onClick={() => setViewMode('custom')}
                size="sm"
                variant={viewMode === 'custom' ? 'default' : 'ghost'}
                className={viewMode === 'custom' ? 'bg-blue-600 text-white' : 'text-blue-300 hover:text-white'}
              >
                <User className="mr-1" size={14} />
                {t('myExercises')}
              </Button>
              <Button
                onClick={() => setViewMode('prebuilt')}
                size="sm"
                variant={viewMode === 'prebuilt' ? 'default' : 'ghost'}
                className={viewMode === 'prebuilt' ? 'bg-blue-600 text-white' : 'text-blue-300 hover:text-white'}
              >
                <BookOpen className="mr-1" size={14} />
                {t('prebuiltExercises')}
              </Button>
              <Button
                onClick={() => setViewMode('all')}
                size="sm"
                variant={viewMode === 'all' ? 'default' : 'ghost'}
                className={viewMode === 'all' ? 'bg-blue-600 text-white' : 'text-blue-300 hover:text-white'}
              >
                <Users className="mr-1" size={14} />
                {t('all')}
              </Button>
            </div>
            
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-400" size={18} />
                <Input
                  placeholder={t('searchExercises')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-blue-800/50 border-blue-600 text-white placeholder:text-blue-400"
                />
              </div>
            </div>
            
            {/* Type Filter */}
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[150px] bg-blue-800/50 border-blue-600 text-white">
                <SelectValue placeholder={t('exerciseType')} />
              </SelectTrigger>
              <SelectContent className="bg-blue-800 border-blue-600">
                <SelectItem value="all">{t('all')}</SelectItem>
                {exerciseTypes.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Discipline Filter */}
            <Select value={filterDiscipline} onValueChange={setFilterDiscipline}>
              <SelectTrigger className="w-[130px] bg-blue-800/50 border-blue-600 text-white">
                <SelectValue placeholder={t('discipline')} />
              </SelectTrigger>
              <SelectContent className="bg-blue-800 border-blue-600">
                <SelectItem value="all">{t('all')}</SelectItem>
                {disciplines.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
            
            {/* Results count */}
            <Badge variant="secondary" className="bg-blue-700/50 text-blue-200">
              {filteredExercises.length} {t('exercises').toLowerCase()}
            </Badge>
            
            {/* Display Mode Toggle */}
            <div className="flex gap-1 border border-blue-600 rounded-md p-0.5">
              <Button
                onClick={() => setDisplayMode('grid')}
                size="sm"
                variant="ghost"
                className={`p-1.5 ${displayMode === 'grid' ? 'bg-blue-600 text-white' : 'text-blue-300 hover:text-white hover:bg-blue-700/50'}`}
              >
                <LayoutGrid size={16} />
              </Button>
              <Button
                onClick={() => setDisplayMode('list')}
                size="sm"
                variant="ghost"
                className={`p-1.5 ${displayMode === 'list' ? 'bg-blue-600 text-white' : 'text-blue-300 hover:text-white hover:bg-blue-700/50'}`}
              >
                <List size={16} />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Exercise Grid/List */}
      {filteredExercises.length > 0 ? (
        displayMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredExercises.map((exercise) => (
            <Card 
              key={exercise.id} 
              className="bg-gradient-to-br from-blue-800/40 to-teal-800/40 border-blue-700/30 backdrop-blur-sm hover:scale-[1.02] transition-transform duration-200"
            >
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <CardTitle className="text-blue-200 text-lg">{exercise.name}</CardTitle>
                    <div className="flex flex-wrap gap-1 mt-2">
                      <Badge className={`${getTypeColor(exercise.type)} text-white text-xs`}>
                        {exercise.type}
                      </Badge>
                      {exercise.target_discipline && (
                        <Badge variant="outline" className="border-teal-500 text-teal-300 text-xs">
                          {exercise.target_discipline}
                        </Badge>
                      )}
                      {exercise.source === 'prebuilt' && (
                        <Badge variant="secondary" className="bg-purple-700/50 text-purple-200 text-xs">
                          {t('prebuiltExercises')}
                        </Badge>
                      )}
                      {exercise.is_public && exercise.source === 'custom' && (
                        <Badge variant="secondary" className="bg-green-700/50 text-green-200 text-xs">
                          <Users size={10} className="mr-1" />
                          Public
                        </Badge>
                      )}
                    </div>
                  </div>
                  {exercise.difficulty_level && (
                    <div className="text-yellow-400 text-xs">
                      {'⭐'.repeat(exercise.difficulty_level)}
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-blue-300 text-sm mb-3 line-clamp-2">{exercise.description}</p>
                
                {/* Metrics */}
                <div className="flex flex-wrap gap-2 mb-3 text-xs text-blue-400">
                  {exercise.duration_minutes && (
                    <span className="flex items-center">
                      <Clock size={12} className="mr-1" />
                      {exercise.duration_minutes} min
                    </span>
                  )}
                  {exercise.repetitions && (
                    <span className="flex items-center">
                      <RotateCcw size={12} className="mr-1" />
                      {exercise.repetitions} reps
                    </span>
                  )}
                  {/* Show distance for dynamic exercises, or duration in seconds for static exercises */}
                  {(exercise.type === 'Static' || exercise.type === 'Breath Hold') ? (
                    exercise.duration_minutes && (
                      <span className="flex items-center text-purple-300">
                        <Clock size={12} className="mr-1" />
                        {exercise.duration_minutes * 60}s
                      </span>
                    )
                  ) : (
                    exercise.distance_meters && (
                      <span className="flex items-center">
                        <Target size={12} className="mr-1" />
                        {exercise.distance_meters}m
                      </span>
                    )
                  )}
                  {exercise.rest_seconds && (
                    <span className="text-green-400">
                      Rest: {exercise.rest_seconds}s
                    </span>
                  )}
                </div>
                
                {/* Category */}
                {exercise.category && (
                  <p className="text-xs text-blue-400 mb-3">
                    {t('exerciseCategory')}: {exercise.category}
                  </p>
                )}
                
                {/* Actions */}
                <div className="flex gap-2">
                  {exercise.source === 'custom' && exercise.created_by === currentUser?.id && (
                    <>
                      <Button
                        onClick={() => openEditModal(exercise)}
                        size="sm"
                        variant="outline"
                        className="border-blue-500 text-blue-300 hover:bg-blue-700/50"
                      >
                        <Edit size={14} className="mr-1" />
                        {t('edit')}
                      </Button>
                      <Button
                        onClick={() => handleDeleteExercise(exercise.id)}
                        size="sm"
                        variant="outline"
                        className="border-red-500 text-red-300 hover:bg-red-700/50"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </>
                  )}
                  <Button
                    onClick={() => duplicateExercise(exercise)}
                    size="sm"
                    variant="outline"
                    className="border-teal-500 text-teal-300 hover:bg-teal-700/50"
                  >
                    <Copy size={14} className="mr-1" />
                    {t('duplicateExercise')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        ) : (
          /* List View */
          <div className="space-y-2">
            {/* List Header */}
            <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-blue-800/30 rounded-lg text-xs font-semibold text-blue-300 uppercase items-center">
              <div className="col-span-3">{t('exerciseName')}</div>
              <div className="col-span-2">
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger
                    className="h-auto p-0 border-0 shadow-none bg-transparent focus:ring-0 text-xs font-semibold uppercase hover:text-white transition-colors [&>svg]:ml-1 [&>svg]:opacity-60"
                    style={{ boxShadow: 'none' }}
                    data-testid="type-filter-header"
                  >
                    <span className={filterType !== 'all' ? 'text-teal-300' : 'text-blue-300'}>
                      <SelectValue placeholder={t('type')} />
                    </span>
                  </SelectTrigger>
                  <SelectContent className="bg-blue-900 border-blue-700 z-50">
                    <SelectItem value="all" className="text-blue-300 font-semibold">
                      {t('type')} — {t('all')}
                    </SelectItem>
                    {exerciseTypes.map(type => (
                      <SelectItem key={type.value} value={type.value} className="text-blue-200">
                        <span className={`inline-block w-2 h-2 rounded-full mr-2 ${getTypeColor(type.value)}`} />
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">{t('discipline')}</div>
              <div className="col-span-1 text-center">{t('duration')}</div>
              <div className="col-span-1 text-center">{t('reps')}</div>
              <div className="col-span-1 text-center">{t('distanceDuration') || 'Dist./Dur.'}</div>
              <div className="col-span-2 text-right">{t('actions')}</div>
            </div>
            
            {/* List Items */}
            {filteredExercises.map((exercise) => (
              <div 
                key={exercise.id}
                className="grid grid-cols-12 gap-2 px-4 py-3 bg-gradient-to-r from-blue-800/30 to-teal-800/30 rounded-lg border border-blue-700/30 items-center hover:bg-blue-700/30 transition-colors"
              >
                <div className="col-span-3">
                  <span className="text-blue-200 font-medium">{exercise.name}</span>
                  {exercise.source === 'prebuilt' && (
                    <Badge variant="secondary" className="ml-2 bg-purple-700/50 text-purple-200 text-xs">
                      {t('prebuilt')}
                    </Badge>
                  )}
                </div>
                <div className="col-span-2">
                  <Badge className={`${getTypeColor(exercise.type)} text-white text-xs`}>
                    {exercise.type}
                  </Badge>
                </div>
                <div className="col-span-2">
                  {exercise.target_discipline ? (
                    <Badge variant="outline" className="border-teal-500 text-teal-300 text-xs">
                      {exercise.target_discipline}
                    </Badge>
                  ) : (
                    <span className="text-blue-400 text-sm">-</span>
                  )}
                </div>
                <div className="col-span-1 text-center text-blue-300 text-sm">
                  {exercise.duration_minutes ? `${exercise.duration_minutes}m` : '-'}
                </div>
                <div className="col-span-1 text-center text-blue-300 text-sm">
                  {exercise.repetitions || '-'}
                </div>
                <div className="col-span-1 text-center text-blue-300 text-sm">
                  {/* Show duration in seconds for Static exercises, distance for others */}
                  {exercise.type === 'Static' || exercise.type === 'Breath Hold' ? (
                    exercise.duration_minutes ? `${exercise.duration_minutes * 60}s` : '-'
                  ) : (
                    exercise.distance_meters ? `${exercise.distance_meters}m` : '-'
                  )}
                </div>
                <div className="col-span-2 flex justify-end gap-1">
                  {exercise.source === 'custom' && (
                    <>
                      <Button
                        onClick={() => openEditModal(exercise)}
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-blue-300 hover:text-white hover:bg-blue-700/50"
                      >
                        <Edit size={14} />
                      </Button>
                      <Button
                        onClick={() => handleDeleteExercise(exercise.id)}
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-red-300 hover:text-white hover:bg-red-700/50"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </>
                  )}
                  <Button
                    onClick={() => duplicateExercise(exercise)}
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-teal-300 hover:text-white hover:bg-teal-700/50"
                  >
                    <Copy size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        <Card className="bg-gradient-to-br from-blue-800/40 to-teal-800/40 border-blue-700/30 backdrop-blur-sm">
          <CardContent className="text-center py-12">
            <Dumbbell size={48} className="mx-auto text-blue-400 mb-4" />
            <h3 className="text-xl font-semibold text-blue-200 mb-2">{t('noCustomExercises')}</h3>
            <p className="text-blue-300 mb-4">Start building your exercise library by creating your first custom exercise.</p>
            <Button 
              onClick={() => { resetForm(); setShowCreateModal(true); }}
              className="bg-gradient-to-r from-blue-600 to-teal-600"
            >
              <Plus className="mr-2" size={18} />
              {t('createCustomExercise')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-gradient-to-br from-blue-900 to-teal-900 border-blue-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-blue-200">{t('editCustomExercise')}</DialogTitle>
            <DialogDescription className="text-blue-300">
              Modify your custom exercise.
            </DialogDescription>
          </DialogHeader>
          {renderExerciseForm(handleUpdateExercise, t('save'))}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExerciseLibraryTab;
